import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import {
  ShieldCheck, Paperclip, Star, Mail, MailOpen, Trash2,
  CornerUpLeft, CornerUpRight, Forward as ForwardIcon,
} from 'lucide-react'

import { deleteMessage, getMe, listMessages, markMessage, MessageSummary } from '../lib/api'

export default function Inbox({ folder = 'inbox' }: { folder?: 'inbox' | 'sent' }) {
  const { mailbox = 'cliff@plustag.email' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: getMe })
  const addresses = me.data?.addresses || []
  const { data, isLoading, error } = useQuery({
    queryKey: ['messages', mailbox, folder],
    queryFn: () => listMessages(mailbox, folder),
  })

  const [menu, setMenu] = useState<{ x: number; y: number; msg: MessageSummary } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const dismiss = () => setMenu(null)
    window.addEventListener('click', dismiss)
    window.addEventListener('scroll', dismiss, true)
    window.addEventListener('resize', dismiss)
    return () => {
      window.removeEventListener('click', dismiss)
      window.removeEventListener('scroll', dismiss, true)
      window.removeEventListener('resize', dismiss)
    }
  }, [])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['messages', mailbox, folder] })

  const mark = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { read?: boolean; starred?: boolean } }) =>
      markMessage(id, mailbox, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['messages', mailbox, folder] })
      const prev = qc.getQueryData<{ messages: MessageSummary[]; next?: string }>(['messages', mailbox, folder])
      if (prev) {
        qc.setQueryData(['messages', mailbox, folder], {
          ...prev,
          messages: prev.messages.map(m => m.message_id === id ? { ...m, ...patch } : m),
        })
      }
      return { prev }
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(['messages', mailbox, folder], ctx.prev),
  })

  const del = useMutation({
    mutationFn: (id: string) => deleteMessage(id, mailbox),
    onSuccess: invalidate,
  })

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-line px-6 py-5">
        <div className="h-eyebrow">{folder === 'sent' ? 'Sent' : 'Mailbox'}</div>
        <div className="flex items-baseline gap-3 flex-wrap">
          {addresses.length > 1 ? (
            <select
              value={mailbox}
              onChange={e => navigate(`/${folder === 'sent' ? 'sent' : 'inbox'}/${encodeURIComponent(e.target.value)}`)}
              className="h-section bg-transparent border-none outline-none cursor-pointer hover:text-ink-700 focus:text-ink-700 pr-6"
            >
              {addresses.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          ) : (
            <h1 className="h-section">{mailbox}</h1>
          )}
          <span className="text-sm text-ink-500">
            {data?.messages.length ?? 0} {folder === 'sent' ? 'sent' : 'message'}{data?.messages.length === 1 ? '' : 's'}
          </span>
          {data && folder !== 'sent' && (
            <span className="text-xs text-ink-500 ml-2">
              {data.messages.filter(m => !m.read).length} unread
            </span>
          )}
        </div>
        <div className="text-xs text-ink-300 mt-1">
          {addresses.length > 1 && 'Switch mailboxes above · '}Right-click a message for actions.
        </div>
      </header>

      {isLoading && <div className="p-6 text-ink-500">Loading…</div>}
      {error && (
        <div className="p-6 text-red-700 bg-red-50 border-b border-red-100">
          Failed to load: {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <ul className="p-4 space-y-2">
          {data?.messages.map(m => (
            <Row key={m.message_id} msg={m} mailbox={mailbox}
                 onContext={(e, msg) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, msg }) }}
                 onToggleRead={() => mark.mutate({ id: m.message_id, patch: { read: !m.read } })}
                 onToggleStar={() => mark.mutate({ id: m.message_id, patch: { starred: !m.starred } })} />
          ))}
          {data && data.messages.length === 0 && (
            <li className="card-inset p-6 text-ink-500 text-sm">
              No messages yet. Mail to <span className="font-mono text-ink-700">{mailbox}</span> will land here within seconds of arrival.
            </li>
          )}
        </ul>
      </div>

      {menu && <ContextMenu
        anchorRef={menuRef}
        x={menu.x} y={menu.y} msg={menu.msg} mailbox={mailbox}
        onClose={() => setMenu(null)}
        onMark={patch => mark.mutate({ id: menu.msg.message_id, patch })}
        onDelete={() => del.mutate(menu.msg.message_id)} />}
    </div>
  )
}

function Row({ msg, mailbox, onContext, onToggleRead, onToggleStar }: {
  msg: MessageSummary
  mailbox: string
  onContext: (e: React.MouseEvent, msg: MessageSummary) => void
  onToggleRead: () => void
  onToggleStar: () => void
}) {
  return (
    <li onContextMenu={e => onContext(e, msg)}>
      <div className={`card p-3 flex gap-3 items-start hover:shadow-lift transition-shadow group
                       ${msg.read ? '' : 'ring-1 ring-pulse/20'}`}>

        <div className="flex flex-col items-center gap-1 pt-0.5 w-6">
          <button onClick={onToggleStar} title={msg.starred ? 'Unstar' : 'Star'}
                  className="text-ink-300 hover:text-pulse">
            <Star size={14} className={msg.starred ? 'fill-pulse text-pulse' : ''} />
          </button>
          {msg.has_attachments && <Paperclip size={13} className="text-ink-300" />}
        </div>

        <Link
          to={`/message/${encodeURIComponent(mailbox)}/${encodeURIComponent(msg.message_id)}`}
          className="flex-1 min-w-0"
        >
          <div className="flex items-baseline gap-3">
            <div className={`truncate text-sm ${msg.read ? 'text-ink-700' : 'text-ink-900 font-semibold'}`}>
              {msg.sender}
            </div>
            <div className="flex-1" />
            <div className="text-[11px] text-ink-500 whitespace-nowrap">
              {msg.received_at ? formatDistanceToNow(new Date(msg.received_at), { addSuffix: true }) : ''}
            </div>
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            {msg.kind === 'dmarc-report' && (
              <span className="inline-flex items-center gap-1 rounded-pill bg-canvas px-2 py-0.5 text-[10px] text-pulse font-medium">
                <ShieldCheck size={10} strokeWidth={2} /> DMARC
              </span>
            )}
            {(() => {
              const viaDomain = msg.delivered_to?.split('@')[1]?.toLowerCase()
              const mboxDomain = mailbox.split('@')[1]?.toLowerCase()
              if (viaDomain && mboxDomain && viaDomain !== mboxDomain) {
                return (
                  <span
                    title={`Delivered to ${msg.delivered_to}`}
                    className="inline-flex items-center rounded-pill bg-canvas px-2 py-0.5 text-[10px] text-ink-500 font-medium"
                  >
                    via {viaDomain}
                  </span>
                )
              }
              return null
            })()}
            <div className={`truncate text-sm ${msg.read ? 'text-ink-700' : 'text-ink-900 font-medium'}`}>
              {msg.subject || <span className="text-ink-300">(no subject)</span>}
            </div>
          </div>
          <div className="mt-0.5 text-xs text-ink-500 truncate">{msg.snippet}</div>
        </Link>

        <button onClick={onToggleRead}
                title={msg.read ? 'Mark unread' : 'Mark read'}
                className="opacity-0 group-hover:opacity-100 transition-opacity self-center p-1 rounded-md hover:bg-canvas text-ink-500">
          {msg.read ? <Mail size={16} /> : <MailOpen size={16} />}
        </button>
      </div>
    </li>
  )
}

function ContextMenu({ x, y, msg, mailbox, onClose, onMark, onDelete, anchorRef }: {
  x: number; y: number
  msg: MessageSummary
  mailbox: string
  onClose: () => void
  onMark: (patch: { read?: boolean; starred?: boolean }) => void
  onDelete: () => void
  anchorRef: React.RefObject<HTMLDivElement>
}) {
  const navigate = useNavigate()
  const go = (path: string) => { onClose(); navigate(path) }

  const linkFor = (mode: 'reply' | 'reply-all' | 'forward') => {
    const qs = new URLSearchParams({
      mode, original_id: msg.message_id, mailbox,
      in_reply_to: msg.message_id,
    })
    if (msg.delivered_to) qs.set('from', msg.delivered_to)
    return `/compose?${qs.toString()}`
  }

  const item = (label: React.ReactNode, onClick: () => void, danger = false) => (
    <button onClick={e => { e.stopPropagation(); onClick() }}
      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5
                  hover:bg-canvas rounded-md
                  ${danger ? 'text-red-700 hover:bg-red-50' : 'text-ink-900'}`}>
      {label}
    </button>
  )

  const style: React.CSSProperties = { top: y, left: x, minWidth: 220 }
  return (
    <div ref={anchorRef}
         onClick={e => e.stopPropagation()}
         className="fixed z-50 card p-1.5 shadow-lift"
         style={style}>
      {item(<><CornerUpLeft size={14} /> Reply</>, () => go(linkFor('reply')))}
      {item(<><CornerUpLeft size={14} /> Reply all</>, () => go(linkFor('reply-all')))}
      {item(<><ForwardIcon size={14} /> Forward</>, () => go(linkFor('forward')))}
      <div className="h-px bg-line my-1" />
      {item(msg.read
        ? <><Mail size={14} /> Mark as unread</>
        : <><MailOpen size={14} /> Mark as read</>,
        () => { onMark({ read: !msg.read }); onClose() })}
      {item(msg.starred
        ? <><Star size={14} className="fill-pulse text-pulse" /> Unstar</>
        : <><Star size={14} /> Star</>,
        () => { onMark({ starred: !msg.starred }); onClose() })}
      <div className="h-px bg-line my-1" />
      {item(<><Trash2 size={14} /> Delete</>, () => { onDelete(); onClose() }, true)}
    </div>
  )
}
